// The /transcripts FilterBar facets (UI-LAW §4b.4). Four facets — Status,
// Source, Occurred, Created — each riding the URL params the screen has always
// used (`?status`, `?source`, `?occurred_after/_before`, `?created_after/
// _before`), so links minted before the FilterBar migration keep working.
//
// Every facet is SERVER-side: `GET /v1/transcripts` takes `statuses`,
// `sources` and the four bound params itself, so the options are the closed
// API enums rather than values harvested from the loaded rows — which is why
// this bar carries no "Loaded rows only" note (§8).

import type { components, operations } from "../../api/schema";
import { facetSearchToValue, facetValueToSearch } from "../../lib/facets";
import {
  TRANSCRIPT_SOURCE_FILTERS,
  TRANSCRIPT_STATUS_FILTERS,
  transcriptSourceFilterList,
  transcriptStatusFilterList,
  type TranscriptsSearch,
} from "../../lib/routerSearch";
import type { FilterFacet, FilterOption, FilterValue } from "../../ui";

type TranscriptSource = components["schemas"]["TranscriptSource"];
type TranscriptStatus = components["schemas"]["TranscriptStatus"];

export type TranscriptListQuery = NonNullable<
  operations["transcriptsList"]["parameters"]["query"]
>;

// The facet ids ARE the param names: a selection facet rides one comma-joined
// param, a range facet the `<id>_after` / `<id>_before` pair (lib/facets).
const SELECTION_FACET_IDS = ["status", "source"] as const;
const RANGE_FACET_IDS = ["occurred", "created"] as const;

/** The facet params this screen owns, for a functional `mergeSearch` patch. */
export type TranscriptFacetPatch = Pick<
  TranscriptsSearch,
  | "created_after"
  | "created_before"
  | "occurred_after"
  | "occurred_before"
  | "source"
  | "status"
>;

// Enum tokens are the wire values; a chip must never read `needs_review`. The
// label is derived from the token rather than hand-listed so a new server enum
// value cannot silently ship as an unlabelled option.
function enumLabel(token: string): string {
  return token
    .split("_")
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(" ");
}

function enumOptions(tokens: readonly string[]): FilterOption[] {
  return tokens.map((value) => ({ label: enumLabel(value), value }));
}

export const TRANSCRIPT_FACETS: FilterFacet[] = [
  {
    id: "status",
    kind: "multi",
    label: "Status",
    options: enumOptions(TRANSCRIPT_STATUS_FILTERS),
  },
  {
    id: "source",
    kind: "multi",
    label: "Source",
    options: enumOptions(TRANSCRIPT_SOURCE_FILTERS),
  },
  { id: "occurred", kind: "dateRange", label: "Occurred" },
  { id: "created", kind: "dateRange", label: "Created" },
];

// The API's upper bounds are EXCLUSIVE (`occurred_at < :occurred_before`),
// while the picker hands back the last day the user INCLUDED, at local
// midnight. So a bound crosses the URL boundary shifted to the next midnight,
// and comes back 1ms earlier — inside the day it names.
function exclusiveBound(iso: string | undefined): string | undefined {
  if (iso === undefined) {
    return undefined;
  }
  const day = new Date(iso);
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate() + 1,
  ).toISOString();
}

function inclusiveBound(iso: string | undefined): string | undefined {
  if (iso === undefined) {
    return undefined;
  }
  const bound = new Date(iso);
  return Number.isNaN(bound.getTime())
    ? undefined
    : new Date(bound.getTime() - 1).toISOString();
}

/** URL search -> the applied `FilterValue` the FilterBar renders. */
export function transcriptFacetValue(search: TranscriptsSearch): FilterValue {
  return facetSearchToValue(
    {
      ...search,
      created_before: inclusiveBound(search.created_before),
      occurred_before: inclusiveBound(search.occurred_before),
    },
    SELECTION_FACET_IDS,
    RANGE_FACET_IDS,
  );
}

/**
 * The applied `FilterValue` -> a search patch. Every facet param is present
 * (cleared ones as `undefined`) so a functional `mergeSearch` drops what is no
 * longer applied while the root-owned `?agent` scope and `?batch` survive.
 */
export function transcriptFacetSearch(
  value: FilterValue,
): TranscriptFacetPatch {
  const patch = facetValueToSearch(value, SELECTION_FACET_IDS, RANGE_FACET_IDS);
  return {
    ...patch,
    created_before: exclusiveBound(patch.created_before),
    occurred_before: exclusiveBound(patch.occurred_before),
  };
}

/**
 * URL params -> the API's own filter shape (docs/09 §7 noun parity). NEVER
 * carries `agent_id`: that joins the query key/params separately so SSE's
 * targeted invalidation keeps hitting the scoped list.
 */
export function transcriptListFilters(
  search: TranscriptsSearch,
): TranscriptListQuery {
  const statuses: TranscriptStatus[] = transcriptStatusFilterList(
    search.status,
  );
  const sources: TranscriptSource[] = transcriptSourceFilterList(search.source);
  return {
    batch_id: search.batch,
    created_after: search.created_after,
    created_before: search.created_before,
    occurred_after: search.occurred_after,
    occurred_before: search.occurred_before,
    sources: sources.length > 0 ? sources : undefined,
    statuses: statuses.length > 0 ? statuses : undefined,
  };
}

/** Filters ride inside the query key: a filter change is a fresh keyset stream. */
export function transcriptFiltersToQueryKey(
  filters: TranscriptListQuery,
): Readonly<Record<string, string | undefined>> {
  return {
    batch_id: filters.batch_id,
    created_after: filters.created_after,
    created_before: filters.created_before,
    occurred_after: filters.occurred_after,
    occurred_before: filters.occurred_before,
    sources: filters.sources?.join(","),
    statuses: filters.statuses?.join(","),
  };
}

/** True when any facet (including the batch strip) narrows the list. */
export function hasActiveTranscriptFacets(
  filters: TranscriptListQuery,
): boolean {
  return Boolean(
    filters.batch_id ||
    filters.created_after ||
    filters.created_before ||
    filters.occurred_after ||
    filters.occurred_before ||
    filters.sources?.length ||
    filters.statuses?.length,
  );
}

/** Every facet param cleared — the patch behind the empty state's Clear. */
export const CLEARED_TRANSCRIPT_FACETS: TranscriptFacetPatch = {
  created_after: undefined,
  created_before: undefined,
  occurred_after: undefined,
  occurred_before: undefined,
  source: undefined,
  status: undefined,
};
